import React, { useState, useEffect } from "react";
import { List } from "antd";
import { useTranslation } from "react-i18next";
import { formatFullName, maskName } from "utils/utils";
import { OpModal } from "components/customAntd/DLS/OpModal/OpModal";

interface SignedInVisitor {
  id: number;
  firstName: string;
  middleName?: string;
  lastName: string;
  company?: string;
  signInTime: string;
}

interface SignedInVisitorSelectionPageProps {
  signedInVisitors: SignedInVisitor[];
  selectedVisitor: SignedInVisitor | null;
  onSelectVisitor: (visitor: SignedInVisitor) => void;
}

const SignedInVisitorSelectionPage: React.FC<
  SignedInVisitorSelectionPageProps
> = ({ signedInVisitors, selectedVisitor, onSelectVisitor }) => {
  const { t } = useTranslation();
  const [selectedVisitorId, setSelectedVisitorId] = useState<number | null>(
    selectedVisitor?.id || null,
  );
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalVisitor, setModalVisitor] = useState<SignedInVisitor | null>(
    null,
  );

  // Update selected visitor when prop changes
  useEffect(() => {
    if (selectedVisitor) {
      setSelectedVisitorId(selectedVisitor.id);
    }
  }, [selectedVisitor]);

  const handleVisitorClick = (visitor: SignedInVisitor) => {
    // Show modal with visitor details
    setModalVisitor(visitor);
    setIsModalVisible(true);
  };

  const handleModalOk = () => {
    if (modalVisitor) {
      setSelectedVisitorId(modalVisitor.id);
      onSelectVisitor(modalVisitor);
    }
    setIsModalVisible(false);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setModalVisitor(null);
  };

  const formatSignInTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatDescription = (visitor: SignedInVisitor) => {
    const parts = [];
    if (visitor.company) {
      parts.push(visitor.company);
    }
    if (visitor.signInTime) {
      parts.push(
        `${t("selfSignIn.signOut.signedInPrefix")}: ${formatSignInTime(visitor.signInTime)}`,
      );
    }
    return parts.join(" • ");
  };

  const renderModalContent = () => {
    if (!modalVisitor) return null;

    const items: any[] = [];

    // Name - always shown
    items.push({
      key: "name",
      label: t("selfSignIn.scheduledVisitor.labels.name"),
      children: formatFullName(
        modalVisitor.firstName,
        modalVisitor.middleName,
        modalVisitor.lastName,
      ),
    });

    // Company
    if (modalVisitor.company) {
      items.push({
        key: "company",
        label: t("selfSignIn.scheduledVisitor.labels.company"),
        children: modalVisitor.company,
      });
    }

    // Sign In Time
    if (modalVisitor.signInTime) {
      items.push({
        key: "signInTime",
        label: t("selfSignIn.signOut.signedInPrefix"),
        children: formatSignInTime(modalVisitor.signInTime),
      });
    }

    return (
      <List
        bordered
        dataSource={items}
        renderItem={(item: any) => (
          <List.Item style={{ display: "block", padding: "12px 16px" }}>
            <div
              style={{
                fontWeight: 600,
                fontSize: "13px",
                color: "#666",
                marginBottom: "4px",
              }}
            >
              {item.label}
            </div>
            <div style={{ fontSize: "15px" }}>{item.children}</div>
          </List.Item>
        )}
      />
    );
  };

  if (signedInVisitors.length === 0) {
    return (
      <div className="workflow-container centered">
        <div className="red-flag-message">
          {t("selfSignIn.signOut.noMatchesFound")}
        </div>
      </div>
    );
  }

  return (
    <div className="workflow-container">
      <div className="selection-message">
        {t("selfSignIn.signOut.selectPrompt")}
      </div>

      <OpModal
        title={t("selfSignIn.scheduledVisitor.modalTitle")}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText={t("selfSignIn.scheduledVisitor.selectButton")}
        cancelText={t("common.cancel")}
        width={600}
      >
        {renderModalContent()}
      </OpModal>

      <div className="visitor-list-container">
        <List
          bordered
          dataSource={signedInVisitors}
          renderItem={(visitor) => (
            <List.Item
              key={visitor.id}
              className={`visitor-list-item ${selectedVisitorId === visitor.id ? "selected" : ""}`}
              onClick={() => handleVisitorClick(visitor)}
            >
              <List.Item.Meta
                title={formatFullName(
                  visitor.firstName,
                  visitor.middleName,
                  maskName(visitor.lastName),
                )}
                description={formatDescription(visitor)}
              />
            </List.Item>
          )}
        />
      </div>
    </div>
  );
};

export default SignedInVisitorSelectionPage;

// Handler function to be called by parent component
export const handleSignedInVisitorSelectionNext = async (
  selectedVisitor: any | null,
): Promise<{ success: boolean }> => {
  if (!selectedVisitor) {
    throw new Error("selfSignIn.signOut.noVisitorSelected");
  }

  return { success: true };
};
